import { DatePicker, message, Pagination, TableColumnsType, Tabs, Typography } from "antd";
import { ContentWrapper, DataTable } from "~/Components";
// import "../../../index.css";
import { Icon } from "@iconify/react";
import { ColumnType } from "antd/lib/table";
import { AxiosInstance } from "axios";
import { debounce } from "lodash";
import moment from "moment";
import Papa from "papaparse";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Action } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { corporateBEApi, corporateBEApiButterflyEffect, corporateBEApiForReviewForm } from "~/Services";
import ActionDropdown, { TypeData } from "./ActionDropdown";
import { ButterFlyTableDrawer } from "./ButterFlyTableDrawer";

export type SortOrder = "ascend" | "descend" | null;

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

export interface ExportParams {
  sortOrder: string;
  orderByName: string;
  searchString: string;
  selectedStatusType: string;
  selectedCardType: string;
  startDateType: string;
  endDateType: string;
}
// Helper function to fetch all data using totalRows
const fetchAllData = async (apiUrl: string, totalRows: number, apiClient: AxiosInstance) => {
  const url = `${apiUrl}&page=0&size=${totalRows}`;

  try {
    const res: any = await apiClient.get(url);

    if (!res?.data?.data?.records) {
      throw new Error("No data received from API");
    }

    return res.data.data.records;
  } catch (err: any) {
    console.error("Fetch all data failed:", err);
    throw new Error("No data received from API");
  }
};

// Helper function to determine if a column is a ColumnType (not a group)
function isColumnType<T>(column: ColumnType<T> | any): column is ColumnType<T> {
  return "dataIndex" in column;
}

const getColumnName = (field: string) => {
  switch (field) {
    case "fullName":
      return "Name";
    case "age":
      return "Age";
    case "gender":
      return "Gender";
    case "pno":
      return "NRIC/Passport Number";
    case "relation":
      return "Relationship with Passenger";
    case "contactCountryCode":
      return "Contact Country Code";
    case "contact":
      return "Contact Number";
    case "nationality":
      return "Nationality";
    default:
      return field;
  }
};

// Updated createExportHandler
export const createExportHandler = async (
  apiClient: AxiosInstance,
  apiUrl: string,
  columns: TableColumnsType<any>,
  params: any,
  exportFileName: string,
  selectedKeys: React.Key[] = [],
  totalRows?: number,
  activeTab?: string
): Promise<void> => {
  try {
    let exportData: any[];
    if (totalRows && selectedKeys.length === 0) {
      exportData = await fetchAllData(apiUrl, totalRows, apiClient);
    } else {
      const response: any = await apiClient.get(apiUrl);
      if (!response?.data?.data?.records) {
        throw new Error("No data received from API");
      }
      exportData = response.data.data.records;
    }

    // Filter by selected keys if provided (using 'requestId')
    if (selectedKeys.length > 0) {
      exportData = exportData.filter((row: any) => selectedKeys.includes(row?.id));
    }

    // Define columns from the provided TableColumnsType, excluding 'actions'
    // const exportColumns = columns.filter(
    //   (col): col is ColumnType<any> => isColumnType(col) && col.dataIndex !== "actions"
    // );

    const emailDetails = {
      title: "Email Details",
      dataIndex: "email",
      key: "email",
      width: 140,
    };

    const exportColumns = [
      {
        title: "Flight Number",
        dataIndex: "flight_No",
        key: "flight_No",
        width: 140,
      },

      {
        title: "Travel Date & Time",
        dataIndex: "DOV",
        key: "DOV",
        width: 140,
      },
      {
        title: "Flight Status",
        dataIndex: "flightStatus",
        key: "flightStatus",
        width: 140,
      },
      {
        title: "Airport",
        dataIndex: "airport",
        key: "airport",
        width: 140,
      },
      {
        title: "Airport Arrival Date & Time",
        dataIndex: "airportArrivalTime",
        key: "airportArrivalTime",
        width: 140,
      },
      {
        title: "Require Calm Room Usage",
        dataIndex: "requireCalmRoomUsage",
        key: "requireCalmRoomUsage",
        render: (value: boolean) => (value ? "Yes" : "No"),
        width: 140,
      },
      {
        title: "Recurring User",
        dataIndex: "recurringUser",
        key: "recurringUser",
        render: (value: boolean) => (value ? "Yes" : "No"),
        width: 140,
      },
    ];
    const closedTabColumns = [
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
        width: 140,
      },
      {
        title: "Closed By",
        dataIndex: "updatedBy",
        key: "updatedBy",
        width: 140,
      },

      {
        title: "Closed At",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 140,
      },
      {
        title: "Attended By",
        dataIndex: "attendedBy",
        key: "attendedBy",
        width: 140,
      },
      {
        title: "Closed Status",
        dataIndex: "status",
        key: "status",
        width: 140,
      },
    ];
    // Define additional nested fields to include
    const nestedFields = {
      disablePassenger: ["fullName", "age", "gender", "pno", "nationality", "contactCountryCode", "contact"],
      guardian: ["fullName", "relation", "contactCountryCode", "contact", "address1", "email"],
    };
    // Combine existing columns with nested fields
    const allColumns: (ColumnType<any> | { title: string; dataIndex: string[] })[] = [
      ...nestedFields.disablePassenger.map((field) => ({
        title: `Disable Passenger ${getColumnName(field)}`,
        dataIndex: ["disablePassenger", field],
      })),
      ...exportColumns,
      ...nestedFields.guardian.map((field) => ({
        title: `${["address1", "email", "relation"].includes(field) ? (field === "relation" ? "" : "Guardian") : "Emergency/Guardian"} ${getColumnName(field)}`,
        dataIndex: ["guardian", field],
      })),
      emailDetails,
      ...(activeTab === "closed" ? closedTabColumns : []),
    ];

    const dateFields = [
      "createdAt",
      "updatedAt",
      "deletedAt",
      "expiryAt",
      "epcJoinedAt",
      "epcUpdatedAt",
      "epcViewedAt",
      "DOV",
      "dov",
    ];

    const formattedData = exportData.map((record: any) => {
      const row: { [key: string]: any } = {};

      allColumns.forEach((col) => {
        const dataIndex = col.dataIndex as string | string[];
        const title = col.title as string;

        // Handle nested dataIndex (e.g., ["disablePassenger", "fullName"])
        let value: any;
        if (Array.isArray(dataIndex)) {
          value = dataIndex.reduce((obj, key) => (obj ? obj[key] : undefined), record);
        } else {
          value = record[dataIndex];
        }

        // Apply custom render function if it exists (only for ColumnType<any>)
        if ("render" in col && col.render && typeof col.render === "function") {
          try {
            const renderedValue = col.render(value, record, 0);
            if (typeof renderedValue !== "object" || renderedValue === null) {
              value = renderedValue;
            } else if (dataIndex === "guardian" && renderedValue) {
              // Special case for guardian contact details
              value = `${record.guardian.contact || ""}\n${record.guardian.email || ""}`;
            }
          } catch (e) {
            console.warn(`Render failed for column ${title}:`, e);
          }
        }

        // Format date fields
        if (typeof dataIndex === "string" && dateFields.includes(dataIndex) && value) {
          value = moment(value).format("YYYY-MM-DD HH:mm:ss");
        }

        row[title] = value ?? "";
      });

      return row;
    });

    if (formattedData.length > 0) {
      const csv = Papa.unparse(formattedData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportFileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      throw new Error("No data selected for export");
    }
  } catch (error) {
    console.error("Error exporting data:", error);
    throw error;
  }
};

export const createFrequencyExportHandler = async (
  apiClient: AxiosInstance,
  apiUrl: string,
  columns: TableColumnsType<any>,
  params: any,
  exportFileName: string,
  selectedKeys: React.Key[] = [],
  totalRows?: number,
  rowKey?: string
): Promise<void> => {
  try {
    let exportData: any[];

    if (totalRows && selectedKeys.length === 0) {
      exportData = await fetchAllData(apiUrl, totalRows, apiClient);
    } else {
      const response: any = await apiClient.get(apiUrl);
      if (!response?.data?.data?.records) {
        throw new Error("No frequency data received from API");
      }
      exportData = response.data.data.records;
    }

    let effectiveRowKey = rowKey;
    if (!effectiveRowKey) {
      if (exportData.length > 0) {
        if ("id" in exportData[0]) effectiveRowKey = "id";
        else if ("nric" in exportData[0]) effectiveRowKey = "nric";
        else if ("key" in exportData[0]) effectiveRowKey = "key";
      }
    }

    if (selectedKeys.length > 0) {
      exportData = exportData.filter((row: any) => selectedKeys.includes((row?.nric ?? "") + (row?.fullName ?? "")));
    }

    const exportColumns = columns.filter(
      (col): col is ColumnType<any> => isColumnType(col) && col.dataIndex !== "actions"
    );

    const dateFields = ["createdAt", "updatedAt"];

    const formattedData = exportData.map((record: any) => {
      const row: { [key: string]: any } = {};

      exportColumns.forEach((col) => {
        const dataIndex = col.dataIndex as string | string[];
        const title = col.title as string;

        let value: any;
        if (Array.isArray(dataIndex)) {
          value = dataIndex.reduce((obj, key) => (obj ? obj[key] : undefined), record);
        } else {
          value = record[dataIndex];
        }

        if ("render" in col && col.render && typeof col.render === "function") {
          try {
            const renderedValue = col.render(value, record, 0);
            if (typeof renderedValue !== "object" || renderedValue === null) {
              value = renderedValue;
            }
          } catch (e) {
            console.warn(`Render failed for column ${title}:`, e);
          }
        }

        if (typeof dataIndex === "string" && dateFields.includes(dataIndex) && value) {
          value = moment(value).format("YYYY-MM-DD HH:mm:ss");
        }

        row[title] = value ?? "";
      });

      return row;
    });

    if (formattedData.length > 0) {
      const csv = Papa.unparse(formattedData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportFileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      throw new Error("No frequency data selected for export");
    }
  } catch (error) {
    console.error("Error exporting frequency data:", error);
    throw error;
  }
};

export const ButterflyEffect = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const activeTab = query.get("activeTab") || "pending";
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchString, setSearchString] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalFrequencyRecords, setTotalFrequencyRecords] = useState<number>(0);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [sortFrequencyOrder, setSortFrequencyOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>(activeTab === "pending" ? "createdAt" : "updatedAt");
  const [orderByFullName, setOrderByFullName] = useState<string>(activeTab === "frequency" ? "frequency" : "");
  const [selectedAirportsType, setSelectedAirportsType] = useState<any>("");
  const [selectedGenderType, setSelectedGenderType] = useState<any>("");
  // const [dateType, setDateType] = useState<any>("");
  const [filterDate, setFilterDate] = useState({ from: "", to: "" });
  const [createdAtType, setCreatedAtType] = useState<any>("");
  const [selectedStatus, setSelectedStatus] = useState<any>("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [frequencyData, setFrequencyData] = useState<React.Key[]>([]);
  const [frequencyDate, setFrequencyDate] = useState({ from: "", to: "" });
  const { openToast } = useNotification();

  const genderType: TypeData[] = [
    {
      label: "Male",
      value: "Male",
    },
    {
      label: "Female",
      value: "Female",
    },
  ];
  const airportsType: TypeData[] = [
    {
      label: "KLIA Terminal 1",
      value: "KLIA Terminal 1",
    },
    {
      label: "KLIA Terminal 2",
      value: "KLIA Terminal 2",
    },
  ];

  const statusType: TypeData[] = [
    {
      label: "Cancel",
      value: "cancel",
    },
    {
      label: "Complete",
      value: "complete",
    },
    {
      label: "No Show",
      value: "noShow",
    },
  ];

  useEffect(() => {
    setOrderByName(activeTab === "pending" ? "createdAt" : "updatedAt");
  }, [activeTab]);

  useEffect(() => {
    setOrderByFullName(activeTab === "frequency" ? "frequency" : "");
  }, [activeTab]);

  const getRecords = useCallback(
    (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
      const status =
        activeTab === "closed" && selectedStatus
          ? selectedStatus
          : activeTab === "closed"
            ? "noShow,cancel,complete"
            : "pending";
      setIsLoading(true);
      let url = `/api/filter/guest-register?page=${currentPage}&size=${20}&orderBy=${orderByParam}&order=${sortOrder}&searchText=${searchString}${
        selectedAirportsType ? `&airport=${selectedAirportsType}` : ""
      }${selectedGenderType ? `&gender=${selectedGenderType}` : ""}${filterDate.from && filterDate.to ? `&DOVStart=${filterDate?.from ?? ""}&DOVEnd=${filterDate?.to ?? ""}` : ""}${
        createdAtType ? `&createdOn=${createdAtType}` : ""
      }&status=${status}`;

      corporateBEApiButterflyEffect
        .get(url)
        .then((res: any) => {
          if (res) {
            let envelopes = res?.data?.data?.records;
            setTotalRecords(res?.data?.data?.count);
            setData(envelopes);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          setData([]);
          setIsLoading(false);
        });
    },
    [selectedAirportsType, selectedGenderType, filterDate, createdAtType, selectedStatus, activeTab]
  );

  useEffect(() => {
    navigate(`/dedicated/passenger/butterfly-effect?activeTab=${query.get("activeTab") ?? "pending"}`, {
      replace: true,
    });
  }, []);

  useEffect(() => {
    if (activeTab === "frequency") {
      setIsLoading(true);
      corporateBEApi
        .get(
          `/api/guest-frequency-list?page=${currentPage}&size=${20}&order=${sortFrequencyOrder}&orderBy=${orderByFullName}&searchText=${searchString}&fromRange=${frequencyDate?.from ?? ""}&toRange=${frequencyDate?.to ?? ""}`
        )
        .then((res: any) => {
          setFrequencyData(res?.data?.data?.records);
          setTotalFrequencyRecords(res?.data?.data?.count);
          setIsLoading(false);
        })
        .catch((err: any) => {
          setIsLoading(false);
          console.log(err);
        });
    }
  }, [activeTab, currentPage, sortFrequencyOrder, orderByFullName, searchString, frequencyDate]);
  // useEffect(() => {
  // setDateType('');
  // setCreatedAtType('');
  // setSelectedAirportsType('');
  // setSelectedGenderType('');
  // setSelectedGenderType('');
  // setCurrentPage(0);
  // setSearchString('');
  // setOrderByName(activeTab==='pending'?'createdAt':'updatedAt');
  // setSortOrder('desc');
  // }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "frequency") {
      getRecords(sortOrder, orderByName, searchString, currentPage);
    }
  }, [getRecords, sortOrder, orderByName, searchString, currentPage, activeTab]);

  const columns: TableColumnsType = [
    {
      title: "Airport",
      dataIndex: "airport",
      key: "airport",
      width: 140,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "airport"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "airport"
            ? "descend"
            : undefined,
    },
    {
      title: "Flight Number",
      dataIndex: "flight_No",
      key: "flight_No",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "flight_No"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "flight_No"
            ? "descend"
            : undefined,
    },
    {
      title: "Date of Visit",
      dataIndex: "DOV",
      key: "DOV",
      render: (text: string) => (text ? moment(text).format("DD MMM YYYY") : ""),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "DOV"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "DOV"
            ? "descend"
            : undefined,
    },
    {
      title: "Full Name",
      dataIndex: ["disablePassenger", "fullName"],
      key: "FULL_NAME",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "FULL_NAME"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "FULL_NAME"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => (text ? moment(text).format("DD MMM YYYY hh:mm:ss A") : ""),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (record: any) => {
        return (
          <Icon
            height={25}
            icon="iconamoon:eye"
            className="text-blue-600 border border-blue-600 rounded-full p-0.5"
            onClick={(e) => {
              e.preventDefault();
              setSelectedRowData?.(record);
              setDrawerVisible?.(true);
            }}
          />
        );
      },
    },
  ];

  const closedColumns: TableColumnsType = [
    {
      title: "Airport",
      dataIndex: "airport",
      key: "airport",
      width: 140,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "airport"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "airport"
            ? "descend"
            : undefined,
    },
    {
      title: "Flight Number",
      dataIndex: "flight_No",
      key: "flight_No",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "flight_No"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "flight_No"
            ? "descend"
            : undefined,
    },
    {
      title: "Date of Visit",
      dataIndex: "DOV",
      key: "DOV",
      render: (text: string) => (text ? moment(text).format("DD MMM YYYY") : ""),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "DOV"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "DOV"
            ? "descend"
            : undefined,
    },
    {
      title: "Full Name",
      dataIndex: ["disablePassenger", "fullName"],
      key: "FULL_NAME",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "FULL_NAME"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "FULL_NAME"
            ? "descend"
            : undefined,
    },
    {
      title: "Closed By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      sorter: true,
      sortOrder: (sortOrder === "asc" && orderByName === "updatedBy"
        ? "ascend"
        : sortOrder === "desc" && orderByName === "updatedBy"
          ? "descend"
          : null) as SortOrder,
    },
    {
      title: "Closed At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text: string) => (text ? moment(text).format("DD MMM YYYY hh:mm:ss A") : ""),
      sorter: true,
      sortOrder: (sortOrder === "asc" && orderByName === "updatedAt"
        ? "ascend"
        : sortOrder === "desc" && orderByName === "updatedAt"
          ? "descend"
          : null) as SortOrder,
    },
    {
      title: "Closed Status",
      dataIndex: "status",
      key: "status",
      render: (status: any) => {
        switch (status) {
          case "noShow":
            return "No Show";
          case "complete":
            return "Complete";
          case "cancel":
            return "Cancel";
          default:
            return status;
        }
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "status"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "status"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (record: any) => {
        return (
          <Icon
            height={25}
            icon="iconamoon:eye"
            className="text-blue-600 border border-blue-600 rounded-full p-0.5"
            onClick={(e) => {
              e.preventDefault();
              setSelectedRowData?.(record);
              setDrawerVisible?.(true);
            }}
          />
        );
      },
    },
  ];

  const frequencyColumns: TableColumnsType = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      sorter: true,
      sortOrder:
        sortFrequencyOrder === "asc" && orderByFullName === "fullName"
          ? "ascend"
          : sortFrequencyOrder === "desc" && orderByFullName === "fullName"
            ? "descend"
            : undefined,
    },
    {
      title: "NRIC/Passport Number",
      dataIndex: "pno",
      render: (text: string) => {
        return text ? text : "-";
      },
      width: 230,
      key: "pno",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "pno"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "pno"
            ? "descend"
            : undefined,
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      sorter: true,
      sortOrder:
        sortFrequencyOrder === "asc" && orderByFullName === "frequency"
          ? "ascend"
          : sortFrequencyOrder === "desc" && orderByFullName === "frequency"
            ? "descend"
            : undefined,
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        setSortFrequencyOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        setSortFrequencyOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column?.key);
    setOrderByFullName(column?.key);
    // setCurrentPage(0);
    // getRecords(order, column.dataIndex, searchString, currentPage);
  };
  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
    setCurrentPage(0);
  }, 1000);

  const handleClose = async (status?: string, attenderName?: string, reason?: string) => {
    // if (!reason) return message.error("Please provide reason for closing");
    try {
      const formdata = {
        formId: selectedRowData.requestId,
        status: status,
        comment: reason,
        attendedBy: attenderName,
      };
      let url = `/api/review-form`;
      corporateBEApiForReviewForm
        .put(url, formdata)
        .then((res) => {
          message.success("Request closed successfully!");
          getRecords(sortOrder, orderByName, searchString, currentPage);
          setDrawerVisible(false);
          setIsLoading(true);
        })
        .catch((err) => {
          message.error("Failed to Close!");
        });
    } catch (error) {
      message.error("Failed to Close!");
    }
  };

  const actions: Action[] = [{ label: "Close Request", onClick: handleClose, type: "primary" }];

  // Updated handleExportData
  async function handleExportData(isExportAll: boolean) {
    try {
      const exportParams = {
        sortOrder,
        orderByName,
        searchString,
        selectedAirportsType,
        selectedGenderType,
        filterDate,
        createdAtType,
        selectedStatus,
      };
      const status =
        activeTab === "closed" && selectedStatus
          ? selectedStatus
          : activeTab === "closed"
            ? "noShow,cancel,complete"
            : "pending";
      const baseApiUrl = `/api/filter/guest-register?orderBy=${orderByName}&order=${sortOrder}&searchText=${searchString}${
        selectedAirportsType ? `&airport=${selectedAirportsType}` : ""
      }${selectedGenderType ? `&gender=${selectedGenderType}` : ""}${filterDate.from && filterDate.to ? `&DOVStart=${filterDate?.from ?? ""}&DOVEnd=${filterDate?.to ?? ""}` : ""}${
        createdAtType ? `&createdOn=${createdAtType}` : ""
      }&status=${status}`;

      const apiUrl = isExportAll ? baseApiUrl : `${baseApiUrl}&page=${currentPage}&size=${currentPage || 10}`;

      await createExportHandler(
        corporateBEApi,
        apiUrl,
        activeTab === "closed" ? closedColumns : columns,
        exportParams,
        "Butterfly_effect_Passenger_details",
        isExportAll ? [] : selectedRowKeys,
        isExportAll ? totalRecords : undefined,
        activeTab
      );
    } catch (error) {
      console.error("Export failed:", error);
      openToast({
        content: "Failed to export data",
        type: "error",
      });
    }
  }

  async function handleExportFrequencyData(isExportAll: boolean) {
    try {
      const exportParams = {
        sortOrder: sortFrequencyOrder,
        orderByName: orderByFullName,
        searchString,
        frequencyDate,
      };
      const baseApiUrl = `/api/guest-frequency-list?page=${currentPage}&order=${sortFrequencyOrder}&orderBy=${orderByFullName}&searchText=${searchString}${
        frequencyDate?.from ? `&fromRange=${frequencyDate.from}` : ""
      }${frequencyDate?.to ? `&toRange=${frequencyDate.to}` : ""}`;
      const apiUrl = isExportAll ? baseApiUrl : `${baseApiUrl}&page=${currentPage}&size=${currentPage || 10}`;
      await createFrequencyExportHandler(
        corporateBEApi,
        apiUrl,
        frequencyColumns,
        exportParams,
        "Butterfly_effect_frequency_details",
        isExportAll ? [] : selectedRowKeys,
        isExportAll ? totalFrequencyRecords : undefined
      );
    } catch (error) {
      console.error("Export failed:", error);
      openToast({
        content: "Failed to export data",
        type: "error",
      });
    }
  }

  const items = [
    {
      label: "Pending",
      key: "pending",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={columns.map((col: any) => ({
              ...col,
              onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
            }))}
            className="table-clickable"
            tableData={
              data
                ? data?.map((d: any) => {
                    return {
                      ...d,
                      key: d?.id,
                    };
                  })
                : []
            }
            isLoading={isLoading}
            exportFileName="Butterfly_effect_Passenger_details"
            exportHandler={handleExportData}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={setSelectedRowKeys}
            totalRecords={totalRecords}
            multiSelect={true}
            hideExport={false}
            isPagination={true}
            recordsPerPage={20}
            isServerSearchApi={true}
            searchPlaceholderText={"Search by Flight Number or Full Name or NRIC/Passport Number"}
            onSearchChange={handleSearchChange}
            onClick={(record) => {
              setSelectedRowData(record);
            }}
            additionalCTA={
              <div className="flex items-center space-x-2">
                <RangePicker
                  allowClear
                  onChange={(date: any) => {
                    if (date) {
                      setFilterDate({
                        from: date?.[0]?.format("YYYY-MM-DD") || "",
                        to: date?.[1]?.format("YYYY-MM-DD") || "",
                      });
                    } else {
                      setFilterDate({
                        from: "",
                        to: "",
                      });
                    }
                  }}
                  value={
                    filterDate.from && filterDate.to ? [moment(filterDate.from), moment(filterDate.to)] : [null, null]
                  }
                  placeholder={["From DOV", "To DOV"]}
                  className="min-w-[180px]"
                />
                <DatePicker
                  value={createdAtType ? moment(createdAtType) : null}
                  onChange={(date, dateString) => {
                    const formattedDate = date ? moment(date).format("YYYY-MM-DD") : "";
                    setCreatedAtType(formattedDate);
                    setCurrentPage(0);
                  }}
                  placeholder="Select Created At"
                  className="mr-2 w-48"
                />
                <ActionDropdown
                  value={selectedAirportsType}
                  options={airportsType}
                  onChange={(e) => {
                    setSelectedAirportsType(e);
                    setCurrentPage(0);
                  }}
                  placeholder="Select Airport"
                />
              </div>
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={currentPage + 1}
              total={totalRecords}
              pageSize={20}
              onChange={(curPage) => {
                let page = curPage - 1;
                setCurrentPage(page);
                // getRecords(sortOrder, orderByName, searchString, page);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Closed",
      key: "closed",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={closedColumns.map((col: any) => ({
              ...col,
              onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
            }))}
            className="table-clickable"
            tableData={
              data
                ? data?.map((d: any) => {
                    return {
                      ...d,
                      key: d?.id,
                    };
                  })
                : []
            }
            isLoading={isLoading}
            exportFileName="CONTRACT"
            exportHandler={handleExportData}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={setSelectedRowKeys}
            totalRecords={totalRecords}
            isPagination={true}
            recordsPerPage={20}
            multiSelect={true}
            hideExport={false}
            isServerSearchApi={true}
            searchPlaceholderText={"Search by Flight Number or Full Name or NRIC/Passport Number"}
            onSearchChange={handleSearchChange}
            onClick={(record) => {
              setSelectedRowData(record);
            }}
            additionalCTA={
              <div className="flex items-center space-x-2">
                <RangePicker
                  allowClear
                  onChange={(date: any) => {
                    if (date) {
                      setFilterDate({
                        from: date?.[0]?.format("YYYY-MM-DD") || "",
                        to: date?.[1]?.format("YYYY-MM-DD") || "",
                      });
                    } else {
                      setFilterDate({
                        from: "",
                        to: "",
                      });
                    }
                  }}
                  value={
                    filterDate.from && filterDate.to ? [moment(filterDate.from), moment(filterDate.to)] : [null, null]
                  }
                  placeholder={["From DOV", "To DOV"]}
                  className="min-w-[180px]"
                />
                <ActionDropdown
                  value={selectedAirportsType}
                  options={airportsType}
                  onChange={setSelectedAirportsType}
                  placeholder="Select Airport"
                />
                <ActionDropdown
                  value={selectedStatus}
                  options={statusType}
                  onChange={setSelectedStatus}
                  placeholder="Select Status"
                />
              </div>
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={currentPage + 1}
              total={totalRecords}
              pageSize={20}
              onChange={(curPage) => {
                let page = curPage - 1;
                setCurrentPage(page);
                // getRecords(sortOrder, orderByName, searchString, page);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Frequency",
      key: "frequency",
      children: (
        <>
          <DataTable
            tabOffset={60}
            columns={frequencyColumns.map((col: any) => ({
              ...col,
              onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
            }))}
            className="table-clickable"
            tableData={
              frequencyData
                ? frequencyData?.map((d: any) => {
                    return {
                      ...d,
                      key: d?.id + "" + d?.fullName,
                    };
                  })
                : []
            }
            isLoading={isLoading}
            exportFileName="CONTRACT"
            exportHandler={handleExportFrequencyData}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={setSelectedRowKeys}
            totalRecords={totalFrequencyRecords}
            isPagination={true}
            recordsPerPage={20}
            multiSelect={true}
            hideExport={false}
            isServerSearchApi={true}
            searchPlaceholderText={"Search by Full Name or NRIC/Passport Number"}
            onSearchChange={handleSearchChange}
            additionalCTA={
              <>
                <RangePicker
                  allowClear
                  onChange={(date: any) => {
                    if (date) {
                      setFrequencyDate({
                        from: date?.[0]?.format("YYYY-MM-DD") || "",
                        to: date?.[1]?.format("YYYY-MM-DD") || "",
                      });
                    } else {
                      setFrequencyDate({
                        from: "",
                        to: "",
                      });
                    }
                  }}
                  value={
                    frequencyDate.from && frequencyDate.to
                      ? [moment(frequencyDate.from), moment(frequencyDate.to)]
                      : [null, null]
                  }
                  placeholder={["From DOV", "To DOV"]}
                  className="min-w-[220px]"
                />
              </>
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={currentPage + 1}
              total={totalFrequencyRecords}
              pageSize={20}
              onChange={(curPage) => {
                let page = curPage - 1;
                setCurrentPage(page);
                // getRecords(sortOrder, orderByName, searchString, page);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
  ];

  return (
    <ContentWrapper
      title={"The Butterfly Effect - User Registration"}
      // loading={drilldownLoading}
    >
      <Tabs
        activeKey={query?.get("activeTab") || ""}
        items={items}
        onChange={(activeKey) => {
          navigate(`${window.location.pathname}?activeTab=${activeKey}`);
          setOrderByName(activeKey === "pending" ? "createdAt" : "updatedAt");
          setFilterDate({ from: "", to: "" });
          setFrequencyDate({ from: "", to: "" });
          setSearchString("");
          setCreatedAtType("");
          setSelectedAirportsType("");
          setSelectedGenderType("");
          setSelectedGenderType("");
          setSelectedStatus("");
          setCurrentPage(0);
          setSearchString("");
          setSortOrder("desc");
        }}
      ></Tabs>
      <ButterFlyTableDrawer
        drawerVisible={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        selectedRowData={selectedRowData}
        actions={activeTab === "pending" ? actions : []}
      />
    </ContentWrapper>
  );
};
